import { env } from "@/lib/env";

/**
 * Mint + cache a bearer JWT from the @ensoo/auth service. The session cookie
 * (set at sign-in) rides cross-origin via `credentials: "include"`, so the
 * auth service authenticates the request and returns a short-lived JWT the
 * gateway can verify. Concurrent callers share one in-flight mint so a burst
 * of queries never stampedes the token endpoint.
 */
let cached: { token: string; expiresAtMs: number } | null = null;
let inflight: Promise<string | null> | null = null;

const EXPIRY_SLACK_MS = 30_000;

function decodeExpMs(token: string): number {
  try {
    const payload = token.split(".")[1] ?? "";
    const json = JSON.parse(atob(payload.replaceAll("-", "+").replaceAll("_", "/"))) as {
      exp?: number;
    };
    return typeof json.exp === "number" ? json.exp * 1000 : Date.now() + 60_000;
  } catch {
    return Date.now() + 60_000;
  }
}

async function mintToken(): Promise<string | null> {
  try {
    const res = await fetch(`${env.ADMIN__AUTH_URL}/api/auth/token`, {
      credentials: "include",
    });
    if (!res.ok) {
      cached = null;
      return null;
    }
    const data = (await res.json()) as { token?: string };
    if (!data.token) return null;
    cached = { token: data.token, expiresAtMs: decodeExpMs(data.token) };
    return data.token;
  } catch {
    return null;
  }
}

export async function getAuthToken(): Promise<string | null> {
  if (cached && cached.expiresAtMs > Date.now() + EXPIRY_SLACK_MS) return cached.token;
  inflight ??= mintToken().finally(() => {
    inflight = null;
  });
  return await inflight;
}

export function clearAuthToken(): void {
  cached = null;
}
